import MidiEffect from './MidiEffect.js';
import {mod} from "utils/mod.js";
import {CYCLE_RESTART, BANG} from 'messaging/message-types.js';
import {DOT, DASH} from './tidal/types.js';
import VariableEffect from './scripting/VariableEffect.js';
import Resolution from 'engine/Resolution.js';
import NodeMessaging from "messaging/NodeMessaging.js";
import {getStep, getLegatoDuration, getLeafNumber} from './tidal/tidal.js';
import {parse, getCycleString} from './tidal/parser.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import MidiTrigger from './MidiTrigger.js';
import WarpEffect from './WarpEffect.js';
import Tone from 'tone';

window.tidalTimes = [];
window.lazyTidals = 0;
window.nolazyTidals = 0;

const TIME_16N = new Tone.Time('16n');
const TIME_32N = new Tone.Time('32n');

class TidalEffect extends NodeMessaging(VariableEffect(WarpEffect)) {
    constructor(track, scriptEffect, object) {
        super(track, false, 5);
        this.zObject = object;
        this.tidalCache = {};
        this.scriptEffect = scriptEffect;
        this.name = 'Tidal';
        this.cycleParameter = new AbstractParameter(
            'cycle', this, (o, x) => {
                if (x) {
                    let values = o.clearVariables();
                    o.handleVariables(x, values);
                    this.tidalCache = {};
                    this.needsUpdate = true;
                }
            },
            {
                defaultValue: undefined,
                min: false,
                max: true,
                unit: '',
                automatable: false
            });

        this.registerParameter(this.cycleParameter);
        this.warpLengthParameter.automatable = false;
        this.warpFactorParameter.automatable = false;
        this.loopLengthParameter.automatable = false;
        this.resolutionParameter.automatable = false;
        this.resolutionParameter.hide = true;
        this.warpLengthParameter.hide = true;
        this.warpFactorParameter.hide = true;
        this.loopLengthParameter.hide = true;
        this.durationFactorParameter.hide = true;
        this.smoothnessParameter.hide = true;

        this.cycleLengthParameter = new AbstractParameter(
            'cycleLength', this, (o, x) => {
                this.tidalCache = {};
            },
            {
                documentation: "The number of 1/32th steps per cycle",
                prettyName: "Cycle Length",
                defaultValue: 32,
                min: 4,
                max: 64,
                unit: ''
            });

        this.registerParameter(this.cycleLengthParameter);

        this.lengthFactorParameter = new AbstractParameter(
            'lengthFactor', this, (o, x) => [],
            {
                documentation: "Multiplies the speed of the cycle",
                defaultValue: 1,
                prettyName: "Speed Factor",
                min: 0.25,
                max: 8,
                unit: ''
            });

        this.registerParameter(this.lengthFactorParameter);

        this.accentParameter = 'velocity';
        
        this.accentFactorParameter = new AbstractParameter(
            'accentFactor', this, (o, x) => [],
            {
                documentation: "Multiplies the speed of the cycle",
                defaultValue: 2,
                prettyName: "Accent Factor",
                min: 0,
                max: 2,
                unit: ''
            });

        this.registerParameter(this.accentFactorParameter);

        this.staccatoFactorParameter = new AbstractParameter(
            'staccatoFactor', this, (o, x) => [],
            {
                documentation: "Multiplies the speed of the cycle",
                defaultValue: 0.2,
                prettyName: "Staccato Factor",
                min: 0,
                max: 2,
                unit: ''
            });

        this.registerParameter(this.staccatoFactorParameter);

        this.cycleRetrigger = true;

        this.resolution = new Resolution(32, false);

        this.cycle = parse('[0 12 ~ 12]');

        // TODO: look through the expression to see how many outputs this needs
        this.numberOfOutputs = 2;

        this.lastLeaf = undefined;

        this.newInlet("cycle length", (msg) => this.onCycleLength(msg[0]));
        this.newInlet("set variable", (msg) => this.onVariableSet(msg[0]));
        this.newOutlet(CYCLE_RESTART);
    }

    lockGroove(x) {
        this.cycleParameter.immutable = x;
        this.cycleLengthParameter.immutable = x;
    }

    onCycleLength(l) {
        if (!isNaN(l)) {
            this.cycleLengthParameter.set(l);
        }
    }

    onVariableSet(m) {
        m = m.split(" ").map(x => x.trim());
        let number = parseFloat(m[1]);
        if (isNaN(number)) {
            return;
        }

        let variable = m[0];
        if (this[variable + "Parameter"]) {
            variable = this[variable + "Parameter"];
            variable.set(number);
        }
        
    }

    clear() {
        super.clear();
        this.lastLeaf = undefined;
        this.lastLeafStep = undefined;
        this.stepNumberTriggered = undefined;
    }

    output(time, stepNumber, actualStepNumber) {
        if (this.scriptEffect) {
            actualStepNumber = this.scriptEffect.actualStepNumber;
        }
        return super.output(time, stepNumber, actualStepNumber);
    }

    get accentFactor() {
        return this.accentFactorParameter.get();
    }
    
    get staccatoFactor() {
        return this.staccatoFactorParameter.get();
    }

    set accentFactor(accent) {
        this.accentFactorParameter.set(accent);
    }
    
    set staccatoFactor(factor) {
        this.staccatoFactorParameter.set(factor);
    }

    get documentation() {
        return [
            "A tidal-cycles inspired scripting effect. Write expressions for cyclic beats using tidal notation.",
            "Type or press enter to execute",
        ];
    }

    isRealTime() {
        return false;
    }

    get cycleLength() {
        return this.cycleLengthParameter.get();
    }

    set cycleLength(cycleLength) {
        return this.cycleLengthParameter.set(cycleLength);
    }

    get lengthFactor() {
        return this.lengthFactorParameter.get();;
    }

    set lengthFactor(len) {
        return this.lengthFactorParameter.set(len);
    }

    get cycle() {
        return this.cycleParameter.get();
    }

    set cycle(cycle) {
        return this.cycleParameter.set(cycle, true, true);
    }

    removeNote(note, sliceNumber, skip=!this.gated) {
        super.removeNote(note, sliceNumber, skip);
    }

    handleVariables(cycle, values=[]) {
        // go thru the cycle and look for variables
        for (let part of cycle) {
            if (Array.isArray(part)) {
                this.handleVariables(part);
            } else {
                if (part.cycle) {
                    this.handleVariables(part.cycle);
                }
                if (part.transpose && typeof part.transpose === "object") {
                    // its a variable
                    this.newVar(part.transpose.name, 0, -32, 32);
                } 
                if (part.operand && typeof part.operand === "object") {
                    this.newVar(part.operand.name, 2, 1, 16);
                }
            }
        }

        let fx = this.scriptEffect || this;
        for (let name in values) {
            let param = fx[name + 'Parameter'];
            if (param) {
                param.copyFrom(
                    values[name], this.track.sequencerEngine);
                if (param.isMapped) {
                    param.isMapped.replaceParameter(param);
                }
            }
        }
        fx.updateVariablesRedux();
    }

    get cycleString() {
        return getCycleString(this.cycle);
    }

    getText() {
        return this.cycleString;
    }

    replaceOperandVariables(cycle) {
        let _cycle = [];
        _cycle.cycleLength = cycle.cycleLength;
        if (!cycle) {
            return _cycle;
        }
        // go thru the cycle and look for variables
        for (let i=0; i < cycle.length; i++) {
            let part =  cycle[i];
            if (Array.isArray(part)) {
                _cycle.push(this.replaceOperandVariables(part));
            } else {
                let _part = {... part};
                if (_part.cycle) {
                    _part.cycle = this.replaceOperandVariables(part.cycle);
                }
                if (part.operand && typeof part.operand === "object") {
                    this.newVar(part.operand.name, 2, 1, 16);
                    _part.operand = this.var(part.operand.name)();
                }
                _cycle.push(_part);
            }
        }
        return _cycle;
    }

    getTrigger(
        stepData,
        note,
        time,
        stepNumber,
        actualStepNumber,
        sliceNumber) {
        let dateA = new Date().getTime();

        if (!this.cycleRetrigger) {
            if (!this.sequencerEngine.isSequenceRolling) {
                stepNumber = this.track.sequencerEngine.trackStep;
            }  else {
                stepNumber = stepNumber;
            }
        }
        if (this.stepNumberTriggered === undefined) {
            if (this.gated || this.sliceRetrigger || this.isNothingPlaying()) {
                this.stepNumberTriggered = stepNumber;
            }
        }
 
        if (this.cycle === undefined) {
            return undefined;
        }

        let stepNumberTriggered = this.stepNumberTriggered;
        if (this.track && this.track.length) {
            let stepNumberOffset = this.track.sequencerEngine.stepNumberOffset || 0;
            stepNumberOffset = stepNumberOffset % this.track.length;
            stepNumberTriggered = mod(stepNumberTriggered - stepNumberOffset, this.track.length);
        }
        let stepsElapsed =  stepNumber - stepNumberTriggered;
        if (stepsElapsed < 0) {
            stepsElapsed  = stepsElapsed + this.track.length;
        }

        /*
        if (!this.cycleRetrigger) {
            stepsElapsed = stepNumber;
        }
        */

        let cacheKey = stepsElapsed % this.cycleLength;

        if (this.scriptEffect) {
            let tidalNumber = this.scriptEffect.tidals.indexOf(this);
        }

        if (cacheKey === 0) {
            if (
                this.scriptEffect && this.scriptEffect.isOutletConnected && 
                    this.scriptEffect.isOutletConnected(CYCLE_RESTART)) {
                let tidalNumber = this.scriptEffect.tidals.indexOf(this);
                this.scriptEffect.sendMessage(
                    CYCLE_RESTART, tidalNumber);
            } else {
                this.sendMessage(
                    CYCLE_RESTART, BANG);
            }
        }

        /*
        if (this.variables.length === 0 && this.tidalCache[cacheKey]) {
            let {triggers, currentCycles} = this.tidalCache[cacheKey];
            triggers.forEach(t => {
                t.time = t.offsetTime + time;
                t.stepNumber = stepNumber;
                t.actualStepNumber = actualStepNumber;
            });
            this.currentCycles = currentCycles;
            window.lazyTidals++;
            return triggers;
        } else {
            window.nolazyTidals++;
        }
        */

        let _cycle = this.replaceOperandVariables(this.cycle);
        let _cycleLength = Math.floor(this.cycleLength / this.lengthFactor);
        let cycleSteps;
        if (this.variables.length === 0 && this.tidalCache[cacheKey]) {
            cycleSteps = this.tidalCache[cacheKey];
        } else {
            cycleSteps = getStep({
                cycle: _cycle,
                stepNumber: stepsElapsed,
                length: _cycleLength
            });
            this.tidalCache[cacheKey] = cycleSteps;
        }


        if (cycleSteps.length > 0) {
            this.currentLeaves = cycleSteps
                .filter(x => x.cycle);

            this.currentCycles = cycleSteps
                .filter(x => x.cycle)
                .reduce((a,b) => {
                    a[b.cycle] = true; 
                    return a;
                }, {});
        } else {
        }
        let triggers = [];

        for (let i in cycleSteps) {
            let leaf = cycleSteps[i];
            let {
                accent,
                pianissimo,
                staccato,
                legato,
                factor,
                outputNumber,
                transpose,
                offset,
                duration,
                flam,
                piano
            } = leaf;
            let isDouble=false;
            if (leaf.rules && leaf.rules.some(x =>
                (x.always &&
                 (Array.isArray(x.always) ? x.always.some(y => y.type === "double") : x.always.type === "double"))
                    ||
                    x.series && x.series.some(y => y.type === "double"))) {
                isDouble=true;
            }

            if (transpose !== null && typeof transpose === "object") {
                // its a variable
                transpose = this.var(transpose.name)();
            }

            stepData = this.getStep(stepData, note, 1, true);
            stepData.slice = sliceNumber;
            stepData.transpose = parseFloat(stepData.transpose) + parseFloat(transpose);
            stepData.duration = duration * this.durationFactor;
            if (stepsElapsed % this.cycleLength === 0) {
                stepData.isNewCycle = true;
            }
                

            if (transpose === null) {
                if (this.lastLeafStep) {
                    stepData.hand = !this.lastLeafStep.hand;
                }
                this.lastLeaf = leaf;
                this.lastLeafStep = stepData;
                continue;
            }

            stepData.isDouble = isDouble;

            if (this.lastLeaf) {
                // get last leaf step

                if (leaf.figureId !== this.lastLeaf.figureId) {
                    let tidalNumber = this.scriptEffect.tidals.indexOf(this);
                    this.scriptEffect.sendMessage(
                        "figure-change", tidalNumber);
                }

                if (leaf.type) {
                    let {type, value} = leaf.type;

                    if (type === DOT) {
                        // if last type was also a DOT then lower
                        // the velocity by 0.9
                        if (this.lastLeaf.type && this.lastLeaf.type.type === DOT) {
                            let vel = this.lastLeafStep.velocity;
                            if (this.isSequenceRolling) {
                                vel = Math.min(2, vel);
                            }
                            stepData.velocity = vel * 0.7;
                        }
                        stepData.hand = !this.lastLeafStep.hand;
                    } else if (type === DASH) {
                        if (value === 0) {
                            stepData.hand = !this.lastLeafStep.hand;
                        } else if (value === 1) {
                            // then it needs to be lower
                            //stepData.velocity = this.lastLeafStep.velocity * 0.15;
                            stepData.hand = this.lastLeafStep.hand;
                        }
                    }
                }
            }

           

            // accent can only come after a dash
            if (accent) {
                stepData.velocity *= this.accentFactor;
            }
            
            if (factor) {
                stepData.velocity *= Math.pow(factor, 2);
            }
            if (pianissimo) {
                stepData.velocity *= 0.05;
            } else if (piano) {
                stepData.velocity *= 0.45;
            }

            if (staccato) {
                stepData.duration *= this.staccatoFactor;
                stepData.release = 5;
                stepData.attack = 3;
            }

            if (legato) {
                stepData.duration = getLegatoDuration(_cycle, leaf.leaf, _cycleLength);
            }

            stepData.on = true;
            stepData.leafNumber = getLeafNumber(_cycle, leaf.leaf, _cycleLength);
            
            let trigger = new MidiTrigger(
                stepData, TIME_32N * offset + time, actualStepNumber, outputNumber);
            trigger.offsetTime = TIME_32N * offset;
            triggers.push(trigger);
            this.lastLeaf = leaf;
            this.lastLeafStep = stepData;

            stepData.velocity = Math.min(2, stepData.velocity);

            if (flam) {
                let flamStep = this.getStep(stepData);
                flamStep.velocity *= 0.8;
                flamStep.duration *= 0.6;
                let flamTrigger = new MidiTrigger(
                    flamStep,
                    trigger.time + TIME_32N*(-0.4),
                    actualStepNumber,
                    outputNumber);

                                                  
                triggers.push(flamTrigger);
            }
        }

        let dateB = new Date().getTime();
        window.tidalTimes.push(dateB - dateA);
        /*
        this.tidalCache[cacheKey] = {
            triggers,
            currentCycles: this.currentCycles
        };
        */
        return triggers;
    }

    getCopy(track, ignoreId=true) {
        let c = new TidalEffect(track);
        c.fromJson(this.getJson(), ignoreId);
        return c;
    }

    getJson(isPreset) {
        return {
            ... super.getJson(isPreset),
            cycleRetrigger: this.cycleRetrigger
        };
    }

    fromJson(json, isCopy) {
        this.cycleParameter.set(json.cycle);
        let bulk = super.fromJson(json, isCopy);
        if (json.cycleRetrigger !== undefined) {
            this.cycleRetrigger = json.cycleRetrigger;
        }
        return bulk;
    }

    hasAccent(cycle=this.cycle) {
        for (let part of cycle) {
            if (Array.isArray(part)) {
                if (this.hasAccent(part) ){
                    return true;
                }
            } else {
                if (part.cycle) {
                    if (this.hasAccent(part.cycle)) {
                        return true;
                    }
                }
                if (part.accent) {
                    return true;
                }
            }
        }
        return false;
    }
}

export default TidalEffect;
